import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React from 'react';
import { DateUtils } from '../../../utils';
import { bomGroupLabel, fmtFeet, windRangeLabel } from '../bom-utils';
import { BOMRecord } from '../types';

const fmt = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

/** Split camel case component names for display (e.g. "AngleIron" → "Angle Iron"). */
const componentLabel = (component: string) => component.replace(/([a-z])([A-Z])/g, '$1 $2');

export interface BOMRecordDetailProps {
  record: BOMRecord;
  productionFeetPerHour?: number;
  fullyBurdenedLaborRate?: number;
  paintCostPerLinearFoot?: number;
}

/**
 * Expanded row of the BOM Specifications grid: every material line with its unit price and cost,
 * the paint and labor calculation, and the record's lookup keys and audit information.
 */
export const BOMRecordDetail: React.FC<BOMRecordDetailProps> = ({
  record,
  productionFeetPerHour,
  fullyBurdenedLaborRate,
  paintCostPerLinearFoot,
}) => {
  const { costs } = record;
  const cellSx = { fontSize: '0.75rem', py: 0.5 };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
        gap: 2,
        px: 5,
        py: 1.5,
        bgcolor: 'grey.50',
      }}
    >
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              {[
                'Component',
                'Material Group',
                'Size / Code',
                'Qty',
                'Unit',
                'Unit Price',
                'Cost',
              ].map(label => (
                <TableCell
                  key={label}
                  align={['Qty', 'Unit Price', 'Cost'].includes(label) ? 'right' : 'left'}
                  sx={{ ...cellSx, fontWeight: 600 }}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {costs.lines.map((line, idx) => (
              <TableRow
                key={`${line.component}-${line.size}-${idx}`}
                sx={line.isMissingPrice ? { bgcolor: '#fff5f5' } : undefined}
              >
                <TableCell sx={cellSx}>{componentLabel(line.component)}</TableCell>
                <TableCell sx={cellSx}>{line.materialGroup ?? 'BOM Hardware'}</TableCell>
                <TableCell sx={{ ...cellSx, fontFamily: 'monospace' }}>{line.size}</TableCell>
                <TableCell align="right" sx={cellSx}>
                  {line.quantity}
                </TableCell>
                <TableCell sx={cellSx}>{line.unit}</TableCell>
                <TableCell align="right" sx={{ ...cellSx, fontFamily: 'monospace' }}>
                  {line.unitPrice != null ? fmt(line.unitPrice) : '—'}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    ...cellSx,
                    fontFamily: 'monospace',
                    color: line.isMissingPrice ? 'error.main' : undefined,
                  }}
                >
                  {line.cost != null ? fmt(line.cost) : 'No price in published version'}
                </TableCell>
              </TableRow>
            ))}
            {costs.lines.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} sx={{ ...cellSx, color: 'text.secondary' }}>
                  No materials on this record.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Typography variant="overline" color="text.secondary">
          Calculation
        </Typography>
        <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.8 }}>
          Paint: {record.nominalSpan} ft ×{' '}
          {paintCostPerLinearFoot != null ? fmt(paintCostPerLinearFoot) : '—'} ={' '}
          {fmt(costs.paintCost)}
          <br />
          Labor hours: ROUND({record.nominalSpan} ÷ {productionFeetPerHour ?? '—'} ×{' '}
          {record.laborProductivityFactor.toFixed(1)}, 1) = {costs.laborHours.toFixed(1)}
          <br />
          Labor cost: {costs.laborHours.toFixed(1)} ×{' '}
          {fullyBurdenedLaborRate != null ? fmt(fullyBurdenedLaborRate) : '—'} ={' '}
          {fmt(costs.laborCost)}
          {record.additionalMarkupPercentage > 0 && (
            <>
              <br />
              Additional markup: {Math.round(record.additionalMarkupPercentage * 100)}%
            </>
          )}
        </Typography>
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 1, pt: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Total cost:{' '}
            {costs.isPriced ? (
              fmt(costs.totalCost)
            ) : (
              <Chip label="Missing Price" size="small" color="error" variant="outlined" />
            )}
          </Typography>
          {record.msrp != null && (
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
              MSRP: {fmt(record.msrp)}
            </Typography>
          )}
        </Box>
        <Typography variant="overline" color="text.secondary" component="div" sx={{ mt: 1 }}>
          Record
        </Typography>
        <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.8 }}>
          {bomGroupLabel(record)} · Risk {record.riskLevel}
          <br />
          Wind {windRangeLabel(record)} · Span {fmtFeet(record.spanFrom)}–{fmtFeet(record.spanTo)}{' '}
          ft
          <br />
          Last changed{' '}
          {DateUtils.parseDate(record.updated ?? record.created).toFormat('MMM d, yyyy')} by SOLTECH
          Support (see audit history)
        </Typography>
      </Paper>
    </Box>
  );
};
